import cx from 'clsx';
import React, { Fragment, useEffect, useState } from 'react';

import { ReactComponent as ArrowIcon } from '@assets/svg/ic-dropdown.svg';
import { ReactComponent as TableSortIcon } from '@assets/svg/table-sort.svg';

import SkeletonBlock from '@components/common/Skeleton/SkeletonBlock';
import TooltipSort from '@components/common/TooltipSort/TooltipSort';
import Button from '@components/uikit/Button/Button';

import { initialSorting } from '@constants/tableSorting';

import useTranslation from '@hooks/useTranslation';

import { selectAppLocationSelectValue } from '@store/useAppStore';

import type { TSourceData } from '../../model/translateSourceDataList';
import { useFacilityAvailableLanguagesListQuery } from '../../services/useFacilityAvailableLanguagesListQuery';
import {
  selectManageTranslationsSearchValue,
  selectManageTranslationsSelectedLanguage,
  useManageTranslationsActions,
} from '../../store/useManageTranslationsStore';
import SelectLanguageMenu from '../SelectLanguageMenu/SelectLanguageMenu';
import TranslationsItem from '../TranslationsItem/TranslationsItem';
import style from './style.module.scss';

const ManageTranslationsTable = () => {
  const { t } = useTranslation();

  const [expandedRows, setExpandedRows] = useState<Set<TSourceData>>(new Set());
  const [sorting, setSorting] = useState(initialSorting);
  const [isOpenLanguageMenu, setIsOpenLanguageMenu] = useState<boolean>(false);

  const locationSelectValue = selectAppLocationSelectValue();
  const selectedLanguage = selectManageTranslationsSelectedLanguage();
  const searchValue = selectManageTranslationsSearchValue();

  const { setManageTranslationsSelectedLanguage } = useManageTranslationsActions();

  const { data: facilityAvailableLanguagesListData, isLoading: facilityAvailableLanguagesListIsLoading } =
    useFacilityAvailableLanguagesListQuery({
      locationId: locationSelectValue.facility_id,
    });

  useEffect(() => {
    if (!locationSelectValue.facility_id) return;

    setManageTranslationsSelectedLanguage(null);
    setExpandedRows(new Set());
  }, [locationSelectValue.facility_id]);

  useEffect(() => {
    if (locationSelectValue.facility_id && !selectedLanguage && facilityAvailableLanguagesListData?.length) {
      setManageTranslationsSelectedLanguage(facilityAvailableLanguagesListData[0]);
    }
  }, [facilityAvailableLanguagesListData, selectedLanguage, locationSelectValue.facility_id]);

  useEffect(() => {
    searchValue.length >= 3 && expandAll();
  }, [searchValue.length]);

  const tableItems: Array<{ name: string; type: TSourceData }> = [
    { name: t('Facility'), type: 'facility' },
    { name: t('Location'), type: 'location' },
    { name: t('Equipment'), type: 'equipment' },
    { name: t('Installation Point'), type: 'installation-point' },
  ];

  const isExpandedAll = expandedRows.size === tableItems.length;

  const SORT_TOOLTIP_ID = 'manage-translations-sort-tooltip';

  const onChangeSorting = (key: string) => {
    if (!sorting.id || sorting.id !== key) {
      setSorting({ id: key, desc: false });
      return;
    }

    const { id, desc } = sorting;

    if (desc) {
      setSorting(initialSorting);
    } else {
      setSorting({ id, desc: true });
    }
  };

  const expandAll = () => setExpandedRows(new Set(tableItems.map((item) => item.type)));

  const collapseAll = () => setExpandedRows(new Set());

  const toggleRow = (type: TSourceData) => {
    setExpandedRows((prevExpandedRows) => {
      const newSelectedRows = new Set(prevExpandedRows);
      if (newSelectedRows.has(type)) {
        newSelectedRows.delete(type);
      } else {
        newSelectedRows.add(type);
      }
      return newSelectedRows;
    });
  };

  return (
    <>
      <table className={style.manageTranslationsTable}>
        <thead>
          {facilityAvailableLanguagesListIsLoading ? (
            <tr>
              <th>
                <SkeletonBlock className={style.skeletonTextClassName} />
              </th>
            </tr>
          ) : (
            <tr className={cx({ [style.rowWithTranslation]: !!facilityAvailableLanguagesListData?.length })}>
              <th className={style.fieldName}>
                <div className={style.sortColumnWrapper}>
                  {t('Field Name')}
                  <TableSortIcon
                    className={cx(style.sortIcon, { [style.activeSort]: sorting.id === 'rowName' })}
                    onClick={() => onChangeSorting('rowName')}
                    data-tooltip-content="rowName"
                    data-tooltip-id={SORT_TOOLTIP_ID}
                  />
                </div>
              </th>
              <th className={style.originalName}>
                <div className={style.sortColumnWrapper}>
                  {t('Original Name')}
                  <TableSortIcon
                    className={cx(style.sortIcon, { [style.activeSort]: sorting.id === 'name' })}
                    onClick={() => onChangeSorting('name')}
                    data-tooltip-content="name"
                    data-tooltip-id={SORT_TOOLTIP_ID}
                  />
                </div>
              </th>
              {!!facilityAvailableLanguagesListData?.length && (
                <th>
                  {facilityAvailableLanguagesListData.length > 1 ? (
                    <>
                      <div
                        className={style.selectLanguageMenuTitle}
                        onClick={() => setIsOpenLanguageMenu(true)}
                        data-tooltip-id="table-equipments-action-menu"
                      >
                        {`${selectedLanguage?.name} (${facilityAvailableLanguagesListData.length} ${t(
                          'Total',
                        )})`}
                        <ArrowIcon
                          className={cx(style.arrowIcon, { [style.arrowIconOpen]: isOpenLanguageMenu })}
                        />
                      </div>

                      <SelectLanguageMenu
                        languages={facilityAvailableLanguagesListData}
                        id="table-equipments-action-menu"
                        isOpen={isOpenLanguageMenu}
                        setIsOpen={setIsOpenLanguageMenu}
                      />
                    </>
                  ) : (
                    <span>{selectedLanguage?.name}</span>
                  )}
                </th>
              )}
              <th className={style.actionsColumn}>
                <Button
                  type="secondary"
                  text={isExpandedAll ? t('Collapse all') : t('Expand all')}
                  onClick={() => {
                    isExpandedAll ? collapseAll() : expandAll();
                  }}
                />
              </th>
            </tr>
          )}
        </thead>
        <tbody>
          {tableItems.map((item) => (
            <Fragment key={item.type}>
              <tr className={style.manageTranslationsTableRow}>
                <td>
                  <span className={style.arrowWrapper} onClick={() => toggleRow(item.type)}>
                    <ArrowIcon
                      className={cx(style.arrowRowIcon, {
                        [style.arrowIconOpen]: expandedRows.has(item.type),
                      })}
                    />
                  </span>
                  <span className={style.name}>{item.name}</span>
                </td>
              </tr>
              {expandedRows.has(item.type) && <TranslationsItem type={item.type} sorting={sorting} />}
            </Fragment>
          ))}
        </tbody>
      </table>
      <TooltipSort id={SORT_TOOLTIP_ID} sorting={sorting} />
    </>
  );
};

export default ManageTranslationsTable;
